import { Box } from '@material-ui/core'
import Snackbars from "components/Snackbar/Snackbar.js";
import CardTable from 'components/CardTable/CardTable'
import { TableInputs } from 'components/CardTable/TableInputs'
import { makeStyles } from "@material-ui/core/styles";
import { style, ColumnContainer } from './styles'
import { useColorTheme } from "hooks/useColorTheme";
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { columns } from './columns'
import { 
  getVolmetricsInv,
  selectVolumetricInv,
  clearVolumetricsInv,
  volumetricInvSearchInputs
} from 'redux/actions/volumetricsInventory/volumetricsInvAction';
import { RemoveRedEye, Iso } from '@material-ui/icons';
import { BLUE_RGB } from "utils/colorsTheme";
import RegularButton from 'components/CustomButtons/Button';
import dayjs from 'dayjs';
import { useSearch } from 'hooks/useSearch';
import { useToggle } from 'hooks/useToggle';
import { dateValidator } from 'helpers/dateValidator';
import { LicensesComponent } from './LicensesComponent';
import { ModalV2 } from 'components/Modal/ModalV2';
import ControlledAccordion from './Accordion';

const blue = BLUE_RGB;
const useStyles = makeStyles(style);

export const VolumetricInventories = () => {
  const dispatch = useDispatch();

  const { setSnackbar, clearSnackbar, clearSearch } = useSearch();
  const [openModal, setOpenModal] = useToggle();

  //selectors
  const { token } = useSelector(state => state.login);
  const { creLicenseSelected, volumetricsInv, volumetricSelected, startDate, endDate } = useSelector(state => state.volumetricInv);
  const { color, message, isOpen, isLoading } = useSelector((state) => state.search);
  const colorBg = useSelector((state) => state.config.colorBg);

  //localState
  const [submitted, setSubmitted] = useState(false)
  //Styles
  const { colorRGB } = useColorTheme(colorBg);
  const classes = useStyles({ color: colorRGB });

  //handlers
  const fetchVolumetrics = useCallback(
    (token, creLicense, start, end) => {
      dispatch(getVolmetricsInv(token, creLicense, start, end));
    }, [dispatch]);

  const handleSubmit = useCallback(() => {
    if (dateValidator(startDate, endDate, 31, setSnackbar)) return;
    if (!creLicenseSelected) {
      setSnackbar("No se ha seleccionado alguna licencia.");
      return;
    }
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");

    fetchVolumetrics(token, creLicenseSelected.value, start, end);
    setSubmitted(true);
  }, [endDate, setSnackbar, startDate, token, creLicenseSelected, fetchVolumetrics]);

  const handleOpenDetails = useCallback((row) => {
    dispatch(selectVolumetricInv(row));
    setOpenModal();
  }, [dispatch, setOpenModal]);

  const columnsTable = useMemo(() => [
    ...columns,
    {
      Header: "Acciones",
      accessor: "id",
      sortable: false,
      filterable: false,
      Cell: (props) => {
        return (
          <ColumnContainer>
            <RegularButton
              round
              justIcon
              tooltip="Detalles Volumetricos"
              color="transparent"
              onClick={() => handleOpenDetails(props.original)}
              className="edit"
            >
              <RemoveRedEye style={{ color: blue }} />
            </RegularButton>
          </ColumnContainer>
        );
      },
    },
  ].map((col) => ({
    ...col,
    headerClassName: `${classes.thTable}`,
  })), [classes, handleOpenDetails]);

  useEffect(() => {
    if (Object.keys(volumetricSelected).length === 0) {
      dispatch(clearVolumetricsInv())
      clearSearch();
    } else {
      dispatch(selectVolumetricInv({}))
    }
  }, [dispatch, clearSearch])

  useEffect(() => {
    const isEmpty = volumetricsInv.length === 0
    if (isEmpty && submitted) {
      setSnackbar("No se encontraron datos en ese rango de fechas");
    }
    setSubmitted(false);
  }, [volumetricsInv])

  const mapVolumetricsInv = useMemo(() =>
    Object.values(volumetricsInv).map(value => {
      const total20c_initial_existence = value.initial_existence.detail.reduce((acc, curr) => acc + curr.liters_20c, 0)
      const total20c_shipmentsEntry = value.shipmentsEntry.detail.reduce((acc, curr) => acc + curr.liters_20c, 0)
      const total20c_platformDischarge = value.platformDischarge.detail.reduce((acc, curr) => acc + curr.liters_20c, 0)
      const total20c_autotankDischarge = value.autotankDischarge.detail.reduce((acc, curr) => acc + curr.liters_20c, 0)
      const total20c_carburetorDischarge = value.carburetorDischarge.detail.reduce((acc, curr) => acc + curr.liters_20c, 0)
      const total20c_final = total20c_initial_existence + total20c_shipmentsEntry - total20c_platformDischarge
        - total20c_autotankDischarge - total20c_carburetorDischarge
      return {
        ...value,
        shipmentsEntry: { ...value.shipmentsEntry, total20c: total20c_shipmentsEntry },
        platformDischarge: { ...value.platformDischarge, total20c: total20c_platformDischarge },
        autotankDischarge: { ...value.autotankDischarge, total20c: total20c_autotankDischarge },
        carburetorDischarge: { ...value.carburetorDischarge, total20c: total20c_carburetorDischarge },
        finalExistence20c: total20c_final
      }
    })
  , [volumetricsInv])

  return (
    <Box>
      <CardTable
        data={mapVolumetricsInv}
        columns={columnsTable}
        loading={isLoading}
        hasHeader
        blockBtn
        title="Inventario Volumétrico"
        icon={<Iso />}
        tableContent={
          <TableInputs
            reducer="volumetricInv"
            dates
            dispatchAction={volumetricInvSearchInputs}
            submitAction={handleSubmit}
            loading={isLoading}
          >
            <LicensesComponent
              state={{ creLicenseSelected }}
              dispatchAction={volumetricInvSearchInputs}
            />
          </TableInputs>
        }
      />

      <ModalV2
        open={openModal}
        onClose={setOpenModal}
        title="Detalles Volumétricos"
        toRight
        halfScreen
        showSnackbar={false}
      >
        <ControlledAccordion />
      </ModalV2>

      <Snackbars
        place="tr"
        color={color}
        message={message}
        open={isOpen}
        closeNotification={clearSnackbar}
        close
      />
    </Box>
  )
}
